import axios from 'axios';
import api from './api';

// Upload image file as multipart/form-data (used by admin CardTypes form).
// The backend endpoint must use multer to accept this.
export async function uploadAndProcessImage(file, uploadUrl) {
  if (!file || !file.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }

  // Read image as bitmap
  const img = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const image = new window.Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  // Draw to canvas with rounded corners (resizes any input to 488x488)
  const canvas = document.createElement('canvas');
  canvas.width = 488;
  canvas.height = 488;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, 488, 488);
  ctx.save();
  ctx.beginPath();
  const r = 30;
  ctx.moveTo(r, 0);
  ctx.lineTo(488 - r, 0);
  ctx.quadraticCurveTo(488, 0, 488, r);
  ctx.lineTo(488, 488 - r);
  ctx.quadraticCurveTo(488, 488, 488 - r, 488);
  ctx.lineTo(r, 488);
  ctx.quadraticCurveTo(0, 488, 0, 488 - r);
  ctx.lineTo(0, r);
  ctx.quadraticCurveTo(0, 0, r, 0);
  ctx.closePath();
  ctx.clip();
  ctx.drawImage(img, 0, 0, 488, 488);
  ctx.restore();

  // Convert canvas to blob
  const processedBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!processedBlob) throw new Error('Failed to process image');

  // Send as multipart/form-data (multer on the backend)
  const formData = new FormData();
  formData.append('background', processedBlob, 'background.png');
  try {
    const res = await api.post(uploadUrl, formData);
    return res.data.fileUrl;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Upload failed');
  }
}

// Upload a canvas-processed PNG as base64 JSON body.
// Used by MyDecks card-form background upload via /api/cards/upload-background-base64.
// No multipart, no multer, no raw body parsing issues — just JSON.
export async function uploadBackgroundBase64(file, uploadUrl) {
  if (!file || !file.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }

  // Read image as bitmap
  const img = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const image = new window.Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  // Draw to canvas with rounded corners (resizes any input to 488x488)
  const canvas = document.createElement('canvas');
  canvas.width = 488;
  canvas.height = 488;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, 488, 488);
  ctx.save();
  ctx.beginPath();
  const r = 30;
  ctx.moveTo(r, 0);
  ctx.lineTo(488 - r, 0);
  ctx.quadraticCurveTo(488, 0, 488, r);
  ctx.lineTo(488, 488 - r);
  ctx.quadraticCurveTo(488, 488, 488 - r, 488);
  ctx.lineTo(r, 488);
  ctx.quadraticCurveTo(0, 488, 0, 488 - r);
  ctx.lineTo(0, r);
  ctx.quadraticCurveTo(0, 0, r, 0);
  ctx.closePath();
  ctx.clip();
  ctx.drawImage(img, 0, 0, 488, 488);
  ctx.restore();

  // Convert canvas to data URL (base64 PNG)
  const dataUrl = canvas.toDataURL('image/png');
  if (!dataUrl || !dataUrl.startsWith('data:image/png;base64,')) {
    throw new Error('Failed to encode image');
  }

  // Send as JSON body: { "image": "data:image/png;base64,..." }
  try {
    const response = await api.post(uploadUrl, { image: dataUrl });
    return response.data.fileUrl;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Upload failed');
  }
}
